import { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Trash2, 
  Copy, 
  Lock, 
  Unlock, 
  ChevronDown,
  Check,
  X,
  Image as ImageIcon
} from 'lucide-react';
import type { WhiteboardElement } from '../../types/whiteboard';

interface Props {
  element: WhiteboardElement;
  isSelected: boolean;
  zoom: number;
  showAnchors?: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<WhiteboardElement>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onAnchorMouseDown?: (elementId: string, anchor: 'top' | 'right' | 'bottom' | 'left') => void;
  onAnchorMouseUp?: (elementId: string, anchor: 'top' | 'right' | 'bottom' | 'left') => void;
}

export function WhiteboardElementRenderer({
  element,
  isSelected,
  zoom,
  showAnchors = false,
  onSelect,
  onUpdate,
  onDelete,
  onDuplicate,
  onAnchorMouseDown,
  onAnchorMouseUp,
}: Props) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, elemX: 0, elemY: 0 });
  const [isEditing, setIsEditing] = useState(false);

  // Drag handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (element.locked) return;
    e.stopPropagation();
    onSelect();
    
    if ((e.target as HTMLElement).closest('.resize-handle')) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      elemX: element.x,
      elemY: element.y,
    });
  }, [element, onSelect]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging && !element.locked) {
      const dx = (e.clientX - dragStart.x) / zoom;
      const dy = (e.clientY - dragStart.y) / zoom;
      onUpdate({
        x: Math.round(dragStart.elemX + dx),
        y: Math.round(dragStart.elemY + dy),
      });
    }
    
    if (isResizing && resizeHandle) {
      const dx = (e.clientX - dragStart.x) / zoom;
      const dy = (e.clientY - dragStart.y) / zoom;
      
      let newX = element.x;
      let newY = element.y;
      let newWidth = element.width;
      let newHeight = element.height;
      
      if (resizeHandle.includes('e')) {
        newWidth = Math.max(40, dragStart.elemX + dx);
      }
      if (resizeHandle.includes('w')) {
        const newW = Math.max(40, element.width - dx);
        newX = element.x + (element.width - newW);
        newWidth = newW;
      }
      if (resizeHandle.includes('s')) {
        newHeight = Math.max(24, dragStart.elemY + dy);
      }
      if (resizeHandle.includes('n')) {
        const newH = Math.max(24, element.height - dy);
        newY = element.y + (element.height - newH);
        newHeight = newH;
      }
      
      onUpdate({ x: newX, y: newY, width: newWidth, height: newHeight });
    }
  }, [isDragging, isResizing, resizeHandle, dragStart, element, zoom, onUpdate]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleResizeStart = (handle: string) => (e: React.MouseEvent) => {
    if (element.locked) return;
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      elemX: element.width,
      elemY: element.height,
    });
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!element.locked) {
      setIsEditing(true);
    }
  };

  // Render element content based on type
  const renderContent = () => {
    const { type, props, state } = element;
    const isDisabled = state === 'disabled';
    const isError = state === 'error';
    const isLoading = state === 'loading';

    switch (type) {
      case 'button':
        return (
          <button
            className={`
              w-full h-full rounded-lg font-medium text-sm transition-all
              flex items-center justify-center gap-2
              ${props.buttonVariant === 'primary' 
                ? 'bg-primary text-white hover:bg-primary-hover' 
                : props.buttonVariant === 'secondary'
                ? 'bg-transparent border border-border-strong text-default'
                : props.buttonVariant === 'danger'
                ? 'bg-danger text-white'
                : 'bg-transparent text-muted hover:text-default'
              }
              ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${isLoading ? 'opacity-70' : ''}
            `}
            disabled={isDisabled}
          >
            {isLoading && <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
            {isEditing ? (
              <input
                type="text"
                value={props.buttonLabel || 'Button'}
                onChange={(e) => onUpdate({ props: { ...props, buttonLabel: e.target.value } })}
                onBlur={() => setIsEditing(false)}
                onKeyDown={(e) => e.key === 'Enter' && setIsEditing(false)}
                className="bg-transparent text-center outline-none w-full"
                autoFocus
              />
            ) : (
              props.buttonLabel || 'Button'
            )}
          </button>
        );

      case 'input':
        return (
          <div className="w-full h-full flex flex-col gap-1">
            {props.inputLabel && (
              <label className="text-xs font-medium text-muted">
                {props.inputLabel}
                {props.required && <span className="text-danger ml-0.5">*</span>}
              </label>
            )}
            <input
              type={props.inputType || 'text'}
              placeholder={props.placeholder}
              className={`
                flex-1 px-3 rounded-input bg-surface-2 border text-sm
                ${isError ? 'border-danger' : 'border-default'}
                ${isDisabled ? 'opacity-50 cursor-not-allowed bg-disabled-bg' : ''}
              `}
              disabled={isDisabled}
            />
          </div>
        );

      case 'dropdown':
        return (
          <div className="w-full h-full flex flex-col gap-1">
            <div className={`
              flex-1 px-3 rounded-input bg-surface-2 border flex items-center justify-between
              ${isError ? 'border-danger' : 'border-default'}
              ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}>
              <span className="text-sm text-muted">{props.dropdownPlaceholder || 'Select...'}</span>
              <ChevronDown className="w-4 h-4 text-muted" />
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <div className={`
              w-5 h-5 rounded border-2 flex items-center justify-center
              ${props.checked ? 'bg-primary border-primary' : 'border-border-strong bg-surface'}
              ${isDisabled ? 'opacity-50' : ''}
            `}>
              {props.checked && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="text-sm text-default">{props.checkboxLabel || 'Checkbox'}</span>
          </label>
        );

      case 'radio_group':
        return (
          <div className="w-full h-full flex flex-col gap-2 py-1">
            {(props.radioOptions || ['Option A', 'Option B']).map((option, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <div className={`
                  w-4 h-4 rounded-full border-2 flex items-center justify-center
                  ${props.radioSelected === i ? 'border-primary' : 'border-border-strong'}
                `}>
                  {props.radioSelected === i && (
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  )}
                </div>
                <span className="text-sm text-default">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'toggle':
        return (
          <div className="flex items-center gap-2">
            <div className={`
              w-10 h-6 rounded-full relative cursor-pointer transition-colors
              ${props.checked ? 'bg-primary' : 'bg-surface-3'}
            `}>
              <div className={`
                absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform
                ${props.checked ? 'translate-x-5' : 'translate-x-1'}
              `} />
            </div>
            {props.checkboxLabel && (
              <span className="text-sm text-default">{props.checkboxLabel}</span>
            )}
          </div>
        );

      case 'table':
        const cols = props.tableColumns || ['Col 1', 'Col 2', 'Col 3'];
        const rows = props.tableRows || 3;
        return (
          <div className="w-full h-full overflow-hidden rounded-lg border border-default">
            <table className="w-full text-xs">
              {props.tableHasHeader && (
                <thead className="bg-surface-2">
                  <tr>
                    {cols.map((col, i) => (
                      <th key={i} className="px-2 py-1.5 text-left font-medium text-muted border-b border-default">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {Array.from({ length: Math.min(rows, 5) }).map((_, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-default last:border-0">
                    {cols.map((_, colIndex) => (
                      <td key={colIndex} className="px-2 py-1.5 text-muted">
                        Cell {rowIndex + 1}-{colIndex + 1}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'text':
        const textStyles: Record<string, string> = {
          h1: 'text-2xl font-bold',
          h2: 'text-xl font-semibold',
          h3: 'text-lg font-semibold',
          h4: 'text-base font-medium',
          body: 'text-sm',
          caption: 'text-xs text-muted',
          label: 'text-xs font-medium uppercase tracking-wider text-muted',
        };
        return (
          <div 
            className={`w-full h-full flex items-center ${textStyles[props.textStyle || 'body']}`}
            style={{ textAlign: props.textAlign || 'left' }}
          >
            {isEditing ? (
              <textarea
                value={props.textContent || 'Text'}
                onChange={(e) => onUpdate({ props: { ...props, textContent: e.target.value } })}
                onBlur={() => setIsEditing(false)}
                className="w-full h-full bg-transparent outline-none resize-none"
                autoFocus
              />
            ) : (
              props.textContent || 'Text content'
            )}
          </div>
        );

      case 'image':
        return (
          <div className="w-full h-full rounded-lg bg-surface-2 border border-dashed border-border-strong flex items-center justify-center">
            {props.imageSrc ? (
              <img 
                src={props.imageSrc} 
                alt={props.imageAlt} 
                className="w-full h-full object-cover rounded-lg"
                style={{ objectFit: props.imageObjectFit }}
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted">
                <ImageIcon className="w-8 h-8" />
                <span className="text-xs">Image Placeholder</span>
              </div>
            )}
          </div>
        );

      case 'shape_rect':
        return (
          <div 
            className="w-full h-full"
            style={{
              backgroundColor: props.shapeFill || '#E2E6F3',
              border: `${props.shapeStrokeWidth || 1}px solid ${props.shapeStroke || '#C9D1EA'}`,
              borderRadius: props.shapeCornerRadius || 8,
            }}
          />
        );

      case 'shape_circle':
        return (
          <div 
            className="w-full h-full rounded-full"
            style={{
              backgroundColor: props.shapeFill || '#E2E6F3',
              border: `${props.shapeStrokeWidth || 1}px solid ${props.shapeStroke || '#C9D1EA'}`,
            }}
          />
        );

      case 'container':
      case 'card':
        return (
          <div 
            className={`w-full h-full rounded-lg ${props.containerBorder ? 'border border-default' : ''}`}
            style={{
              backgroundColor: props.containerBackground || (type === 'card' ? '#FFFFFF' : 'transparent'),
              padding: props.containerPadding || 16,
              boxShadow: props.containerShadow ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <div className="w-full h-full border-2 border-dashed border-border rounded flex items-center justify-center text-xs text-muted">
              {type === 'card' ? 'Card Content' : 'Container Content'}
            </div>
          </div>
        );

      case 'modal':
        return (
          <div className="w-full h-full rounded-xl bg-white shadow-xl border border-default overflow-hidden">
            <div className="h-10 bg-surface-2 border-b border-default flex items-center justify-between px-3">
              <span className="text-sm font-medium">Modal Title</span>
              <button className="w-6 h-6 rounded hover:bg-surface-3 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 flex-1 flex items-center justify-center text-xs text-muted">
              Modal Content
            </div>
          </div>
        );

      case 'tabs':
        const tabOptions = props.dropdownOptions || ['Tab 1', 'Tab 2', 'Tab 3'];
        return (
          <div className="w-full h-full flex border-b border-default">
            {tabOptions.map((tab, i) => (
              <button
                key={i}
                className={`
                  px-4 h-full text-sm font-medium border-b-2 transition-colors
                  ${props.radioSelected === i 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted hover:text-default'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </div>
        );

      case 'list':
        const listItems = props.dropdownOptions || ['Item 1', 'Item 2', 'Item 3'];
        return (
          <div className="w-full h-full overflow-hidden">
            {listItems.map((item, i) => (
              <div 
                key={i}
                className="px-3 py-2 border-b border-default last:border-0 text-sm hover:bg-surface-2 cursor-pointer"
              >
                {item}
              </div>
            ))}
          </div>
        );

      case 'accordion':
        const sections = props.dropdownOptions || ['Section 1', 'Section 2'];
        return (
          <div className="w-full h-full divide-y divide-default">
            {sections.map((section, i) => (
              <div key={i}>
                <div className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-surface-2">
                  <span className="text-sm font-medium">{section}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${i === 0 ? 'rotate-180' : ''}`} />
                </div>
                {i === 0 && (
                  <div className="px-3 py-2 text-xs text-muted bg-surface-2">
                    Accordion content area
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'icon':
        return (
          <div className="w-full h-full flex items-center justify-center text-2xl">
            {props.textContent || '★'}
          </div>
        );

      default:
        return (
          <div className="w-full h-full flex items-center justify-center text-xs text-muted bg-surface-2 rounded">
            {element.type}
          </div>
        );
    }
  };

  return (
    <div
      ref={elementRef}
      data-element-id={element.id}
      className={`
        absolute group
        ${isSelected ? 'z-50' : ''}
        ${isDragging ? 'cursor-grabbing' : element.locked ? 'cursor-not-allowed' : 'cursor-grab'}
        ${!element.visible ? 'opacity-30' : ''}
      `}
      style={{
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height,
        zIndex: element.zIndex,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {/* Element content */}
      <div className={`
        w-full h-full
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${element.locked ? 'pointer-events-none' : ''}
      `}>
        {renderContent()}
      </div>

      {/* Selection UI */}
      {isSelected && !element.locked && (
        <>
          {/* Resize handles */}
          <div className="resize-handle absolute -top-1 -left-1 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-nw-resize" onMouseDown={handleResizeStart('nw')} />
          <div className="resize-handle absolute -top-1 -right-1 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-ne-resize" onMouseDown={handleResizeStart('ne')} />
          <div className="resize-handle absolute -bottom-1 -left-1 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-sw-resize" onMouseDown={handleResizeStart('sw')} />
          <div className="resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-se-resize" onMouseDown={handleResizeStart('se')} />
          <div className="resize-handle absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-n-resize" onMouseDown={handleResizeStart('n')} />
          <div className="resize-handle absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-s-resize" onMouseDown={handleResizeStart('s')} />
          <div className="resize-handle absolute top-1/2 -left-1 -translate-y-1/2 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-w-resize" onMouseDown={handleResizeStart('w')} />
          <div className="resize-handle absolute top-1/2 -right-1 -translate-y-1/2 w-3 h-3 bg-white border-2 border-primary rounded-sm cursor-e-resize" onMouseDown={handleResizeStart('e')} />

          {/* Quick actions toolbar */}
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-1 px-1.5 py-1 bg-surface rounded-lg shadow-lg border border-default">
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="p-1.5 rounded hover:bg-surface-2 text-muted hover:text-default"
              title="Duplicate"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onUpdate({ locked: !element.locked }); }}
              className="p-1.5 rounded hover:bg-surface-2 text-muted hover:text-default"
              title={element.locked ? 'Unlock' : 'Lock'}
            >
              {element.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            </button>
            <div className="w-px h-4 bg-border" />
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 rounded hover:bg-danger-dim text-muted hover:text-danger"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </>
      )}

      {/* Lock indicator */}
      {element.locked && (
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-warning rounded-full flex items-center justify-center">
          <Lock className="w-3 h-3 text-white" />
        </div>
      )}

      {/* Anchor points for arrow connections */}
      {showAnchors && (
        <>
          {(['top', 'right', 'bottom', 'left'] as const).map(anchor => {
            const posStyle = (() => {
              switch (anchor) {
                case 'top': return { left: '50%', top: -4, transform: 'translateX(-50%)' };
                case 'bottom': return { left: '50%', bottom: -4, transform: 'translateX(-50%)' };
                case 'left': return { top: '50%', left: -4, transform: 'translateY(-50%)' };
                case 'right': return { top: '50%', right: -4, transform: 'translateY(-50%)' };
              }
            })();

            return (
              <div
                key={anchor}
                className="absolute w-3 h-3 rounded-full bg-primary border-2 border-white shadow cursor-crosshair z-50 opacity-0 group-hover:opacity-100 hover:scale-125 transition-all"
                style={posStyle}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onAnchorMouseDown?.(element.id, anchor);
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  onAnchorMouseUp?.(element.id, anchor);
                }}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

